import { useState } from "react";
import { useGlobalStacks } from "@/state/globalStacks";
import { HGRecordUrl } from "@/common/types";
import RecordSheet from "./RecordSheet";
import { useCollection, useIsOwnSpace } from "@/api/hooks";
import { HGStacks } from "@/stack/stacks";
import { RecordsMode } from "@/state/recordsView";
import { Plus } from "lucide-react";

function getEmptyRecord(
  collectionIDs: string[],
  stacks: HGStacks,
  withIntervals: boolean,
) {
  return {
    title: "",
    content: "",
    collection_id: collectionIDs,
    url: [] as HGRecordUrl[],
    interval: withIntervals ? stacks.getInterval() : undefined,
    end_interval: withIntervals
      ? stacks.getEndInterval() || stacks.getInterval()
      : undefined,
  };
}

interface RecordNewProps {
  /** Which kind of record to create; mirrors the band's active tab. */
  mode: RecordsMode;
}

/**
 * The row that starts a new record, directly above the list it will join.
 *
 * A row rather than an icon in the chrome: it is the one thing on the page that
 * *adds* to what is below it, and a full-width line above the list says that
 * where a 30px glyph among the view controls did not. Identical on both tabs —
 * a note is a record that has no place on the time axis, and nothing about
 * writing one should look different.
 *
 * Renders nothing in someone else's space, so the row is never an affordance
 * that only 404s.
 */
export default function RecordNew({ mode }: RecordNewProps) {
  const collection = useCollection();
  const globalStacks = useGlobalStacks();
  const isOwnSpace = useIsOwnSpace();
  const [open, setOpen] = useState(false);

  // Someone else's page is read-only: the record would have to go into one of
  // their collections, which the API rejects.
  if (!isOwnSpace) return null;

  const collectionIDs = collection ? [collection.id] : [];
  const label = mode === "notes" ? "New note" : "New record";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // Left inset matches the band's accent rule and the timeline's spine,
        // so the plus sits on the same rail as everything below it.
        className="group flex h-[34px] w-full shrink-0 items-center gap-2 border-b border-border bg-background pl-[18px] pr-2 text-left transition-colors hover:bg-row-hover"
      >
        <Plus className="size-[13px] shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
        <span className="truncate text-[12.5px] text-muted-foreground transition-colors group-hover:text-foreground">
          {label}
        </span>
      </button>

      <RecordSheet
        record={getEmptyRecord(collectionIDs, globalStacks, mode === "time")}
        title={label}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}
